/**
 * Fridges & freezers out of service. A paused unit asks for no readings and
 * is never "Missed"; logs are never touched. Express owns the periods —
 * day items carry `outOfService: { since, sinceLocalDate, until,
 * untilLocalDate, reason, startedByName }` for any day it was paused.
 */

import dayjs from 'dayjs'
import timezonePlugin from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import {
  FOOD_SAFETY_DEFAULT_TIMEZONE,
  FOOD_SAFETY_OUT_OF_SERVICE_BACKDATE_MAX_DAYS,
  FOOD_SAFETY_OUT_OF_SERVICE_REASON_MAX_LENGTH,
  FOOD_SAFETY_OUT_OF_SERVICE_SECTIONS,
} from 'src/constants/foodSafety'
import {
  formatFoodSafetyClockTime,
  normalizeFoodSafetyTimeText,
} from 'src/utils/foodSafetyTime'

dayjs.extend(utc)
dayjs.extend(timezonePlugin)

export { FOOD_SAFETY_OUT_OF_SERVICE_REASON_MAX_LENGTH }

/** When the unit stopped working: now (default) or an earlier time. */
export const FOOD_SAFETY_OUT_OF_SERVICE_START = Object.freeze({
  NOW: 'now',
  EARLIER: 'earlier',
})

export function canMarkFoodSafetyOutOfService(definition) {
  return (
    FOOD_SAFETY_OUT_OF_SERVICE_SECTIONS.includes(definition?.section) &&
    definition?.active !== false
  )
}

/** The unit's open period (no end yet), from a definition. */
export function getOpenFoodSafetyOutOfServicePeriod(definition) {
  const periods = Array.isArray(definition?.outOfServicePeriods)
    ? definition.outOfServicePeriods
    : []
  return periods.find(period => period && !period.endAt) || null
}

/**
 * Still paused on `localDate` — readings aren't asked for. A unit returned
 * to service later that day takes readings again.
 */
export function isFridgeItemPausedOnDay(item, localDate) {
  const oos = item?.outOfService
  if (!oos) return false
  if (!oos.until) return true
  return Boolean(
    oos.untilLocalDate && localDate && oos.untilLocalDate > localDate,
  )
}

/** Out of service now (open period) — "Return to service" applies. */
export function isFridgeItemOutOfServiceNow(item) {
  return Boolean(item?.outOfService && !item.outOfService.until)
}

const LOCAL_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/

function formatLocalDateShort(localDate, todayLocalDate) {
  const match = LOCAL_DATE_RE.exec(String(localDate || ''))
  if (!match) return ''
  if (localDate === todayLocalDate) return 'today'
  const day = new Date(Date.UTC(+match[1], +match[2] - 1, +match[3], 12))
  const today = LOCAL_DATE_RE.exec(String(todayLocalDate || ''))
  if (today) {
    const todayMs = Date.UTC(+today[1], +today[2] - 1, +today[3], 12)
    if (todayMs - day.getTime() === 24 * 60 * 60 * 1000) return 'yesterday'
  }
  return day.toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  })
}

/**
 * "Out of service since yesterday — Compressor failed · Sam".
 * Accepts a day item's `outOfService` or a definition period.
 */
export function formatFoodSafetyOutOfServiceSince(
  outOfService,
  { todayLocalDate } = {},
) {
  if (!outOfService) return ''
  const sinceLocalDate =
    outOfService.sinceLocalDate || outOfService.startLocalDate
  const since = outOfService.since || outOfService.startAt
  const when =
    sinceLocalDate === todayLocalDate
      ? `today${
          formatFoodSafetyClockTime(since)
            ? ` ${formatFoodSafetyClockTime(since)}`
            : ''
        }`
      : formatLocalDateShort(sinceLocalDate, todayLocalDate)
  const reason = String(outOfService.reason || '').trim()
  const who = outOfService.startedByName
  return [
    `Out of service${when ? ` since ${when}` : ''}`,
    reason ? ` — ${reason}` : '',
    who ? ` · ${who}` : '',
  ].join('')
}

/** "Back in service at 14:05" for a unit returned on the viewed day. */
export function formatFoodSafetyBackInService(outOfService) {
  const time = formatFoodSafetyClockTime(outOfService?.until)
  return time
    ? `Was out of service earlier — back in service at ${time}`
    : 'Was out of service earlier — back in service'
}

export function validateFoodSafetyOutOfServiceReason(reason) {
  const text = String(reason || '').trim()
  if (!text) return 'Say why it’s out of service.'
  if (text.length > FOOD_SAFETY_OUT_OF_SERVICE_REASON_MAX_LENGTH) {
    return `Keep it under ${FOOD_SAFETY_OUT_OF_SERVICE_REASON_MAX_LENGTH} characters.`
  }
  return null
}

const resolveTimezone = timezone => {
  if (!timezone) return FOOD_SAFETY_DEFAULT_TIMEZONE
  try {
    dayjs().tz(timezone)
    return timezone
  } catch (err) {
    return FOOD_SAFETY_DEFAULT_TIMEZONE
  }
}

/** Today in the venue's time, "YYYY-MM-DD". */
export function foodSafetyVenueToday(timezone, nowMs = Date.now()) {
  return dayjs(nowMs).tz(resolveTimezone(timezone)).format('YYYY-MM-DD')
}

/**
 * Days the start can be on, newest first: today for everyone, and the
 * previous 7 days for managers.
 * @returns {{ value: string, label: string }[]}
 */
export function buildFoodSafetyOutOfServiceDayOptions({
  todayLocalDate,
  canManage = false,
}) {
  if (!LOCAL_DATE_RE.test(String(todayLocalDate || ''))) return []
  const days = canManage ? FOOD_SAFETY_OUT_OF_SERVICE_BACKDATE_MAX_DAYS : 0
  const today = dayjs(todayLocalDate)
  return Array.from({ length: days + 1 }, (_, i) => {
    const value = today.subtract(i, 'day').format('YYYY-MM-DD')
    const short = formatLocalDateShort(value, todayLocalDate)
    return {
      value,
      label: short.charAt(0).toUpperCase() + short.slice(1),
    }
  })
}

/** "7:30" / "0730" / "07:30" → "07:30"; anything else → null. */
export function normalizeFoodSafetyClockInput(raw) {
  return normalizeFoodSafetyTimeText(raw)
}

/** The start as an ISO instant, reading the day and time in venue time. */
export function buildFoodSafetyOutOfServiceStart({
  localDate,
  time,
  timezone,
}) {
  const clock = normalizeFoodSafetyClockInput(time)
  if (!clock || !LOCAL_DATE_RE.test(String(localDate || ''))) return null
  return dayjs
    .tz(`${localDate} ${clock}`, 'YYYY-MM-DD HH:mm', resolveTimezone(timezone))
    .toISOString()
}

/**
 * Checks an earlier start before it goes to Express (which checks again).
 * @returns {{ field: 'time'|'backdateReason', message: string }|null}
 */
export function validateFoodSafetyOutOfServiceStart({
  localDate,
  time,
  backdateReason,
  todayLocalDate,
  timezone,
  nowMs = Date.now(),
}) {
  const start = buildFoodSafetyOutOfServiceStart({ localDate, time, timezone })
  if (!start) {
    return { field: 'time', message: 'Enter a time like 07:30.' }
  }
  if (new Date(start).getTime() > nowMs) {
    return { field: 'time', message: 'That time hasn’t happened yet.' }
  }
  if (localDate < todayLocalDate) {
    const text = String(backdateReason || '').trim()
    if (!text) {
      return {
        field: 'backdateReason',
        message: 'Say why it’s being recorded on a later day.',
      }
    }
    if (text.length > FOOD_SAFETY_OUT_OF_SERVICE_REASON_MAX_LENGTH) {
      return {
        field: 'backdateReason',
        message: `Keep it under ${FOOD_SAFETY_OUT_OF_SERVICE_REASON_MAX_LENGTH} characters.`,
      }
    }
  }
  return null
}

/**
 * What the start means for that day's check, e.g. "Today's 10:00 check won't
 * count as missed." Null when there's no due time or no valid start.
 */
export function describeFoodSafetyOutOfServiceStartEffect({
  dueByTime,
  localDate,
  time,
  todayLocalDate,
}) {
  const due = normalizeFoodSafetyClockInput(dueByTime)
  const clock = normalizeFoodSafetyClockInput(time)
  if (!due || !clock || !localDate) return null
  const day = `${formatLocalDateShort(localDate, todayLocalDate)}’s`
  return clock <= due
    ? `${
        day.charAt(0).toUpperCase() + day.slice(1)
      } ${due} check won’t count as missed.`
    : `It broke after ${due}, so ${day} check was still due.`
}
